import type { CalendarOccurrence } from '@fc/core'
import { Badge, Button, Card, Spinner, cn } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, ChevronLeft, ChevronRight, List, MapPin } from 'lucide-react'
import { useMemo, useState } from 'react'
import { PageHeader } from '../../components/page-header'
import { getCalendarRange } from '../../lib/calendar-api'
import {
  MONTH_LABELS,
  WEEKDAY_LABELS,
  addDays,
  addMonths,
  dayKey,
  endOfMonthGrid,
  formatDayHeading,
  formatTime,
  isSameDay,
  isSameMonth,
  startOfDay,
  startOfMonth,
  startOfMonthGrid,
} from './date-utils'

type ViewMode = 'month' | 'agenda'

const statusVariant: Record<string, 'success' | 'neutral' | 'gold' | 'danger'> = {
  scheduled: 'success',
  completed: 'neutral',
  canceled: 'danger',
}

/** Group occurrences into a Map keyed by local day (YYYY-MM-DD). */
function bucketByDay(occurrences: CalendarOccurrence[]): Map<string, CalendarOccurrence[]> {
  const map = new Map<string, CalendarOccurrence[]>()
  for (const occ of occurrences) {
    const key = dayKey(new Date(occ.startsAt))
    const bucket = map.get(key)
    if (bucket) bucket.push(occ)
    else map.set(key, [occ])
  }
  return map
}

export function CalendarPage() {
  const [view, setView] = useState<ViewMode>('month')
  // `cursor` is the first-of-month anchor for the visible range.
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()))

  // Month view shows the full 6-week grid; agenda shows the same window so the
  // two views stay in sync and reuse one query.
  const range = useMemo(() => {
    const from = startOfMonthGrid(cursor)
    const to = endOfMonthGrid(cursor)
    return { from, to }
  }, [cursor])

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['calendar', range.from.toISOString(), range.to.toISOString()],
    queryFn: () => getCalendarRange({ from: range.from.toISOString(), to: range.to.toISOString() }),
  })

  const byDay = useMemo(() => bucketByDay(data?.occurrences ?? []), [data])
  const today = startOfDay(new Date())

  const monthLabel = `${MONTH_LABELS[cursor.getMonth()]} ${cursor.getFullYear()}`

  return (
    <div className="space-y-6">
      <PageHeader
        title="Calendar"
        description="Scheduled events and gatherings across your church."
        actions={
          <div
            className="flex items-center gap-1 rounded-lg border border-border bg-card p-0.5"
            role="tablist"
            aria-label="Calendar view"
          >
            <Button
              variant={view === 'month' ? 'secondary' : 'ghost'}
              size="sm"
              role="tab"
              aria-selected={view === 'month'}
              onClick={() => setView('month')}
            >
              <CalendarDays />
              Month
            </Button>
            <Button
              variant={view === 'agenda' ? 'secondary' : 'ghost'}
              size="sm"
              role="tab"
              aria-selected={view === 'agenda'}
              onClick={() => setView('agenda')}
            >
              <List />
              Agenda
            </Button>
          </div>
        }
      />

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="icon-sm"
            aria-label="Previous month"
            onClick={() => setCursor((c) => addMonths(c, -1))}
          >
            <ChevronLeft />
          </Button>
          <Button
            variant="secondary"
            size="icon-sm"
            aria-label="Next month"
            onClick={() => setCursor((c) => addMonths(c, 1))}
          >
            <ChevronRight />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setCursor(startOfMonth(new Date()))}>
            Today
          </Button>
        </div>
        <h2 className="font-display text-lg font-semibold tracking-tight" aria-live="polite">
          {monthLabel}
        </h2>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center gap-2 px-6 py-20 text-muted">
          <Spinner /> Loading calendar…
        </Card>
      ) : isError ? (
        <Card className="space-y-2 px-6 py-20 text-center">
          <p className="text-sm text-danger">Couldn’t load the calendar.</p>
          <p className="text-xs text-dim">
            {error instanceof Error ? error.message : 'Is the API running on :3000?'}
          </p>
        </Card>
      ) : view === 'month' ? (
        <MonthGrid cursor={cursor} byDay={byDay} today={today} />
      ) : (
        <AgendaList
          occurrences={data?.occurrences ?? []}
          rangeFrom={range.from}
          rangeTo={range.to}
        />
      )}
    </div>
  )
}

function MonthGrid({
  cursor,
  byDay,
  today,
}: {
  cursor: Date
  byDay: Map<string, CalendarOccurrence[]>
  today: Date
}) {
  const gridStart = startOfMonthGrid(cursor)
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i))

  return (
    <Card className="overflow-hidden p-0">
      <div className="grid grid-cols-7 border-b border-border bg-card-hover">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-dim"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursor)
          const isToday = isSameDay(day, today)
          const dayOccs = byDay.get(dayKey(day)) ?? []
          return (
            <div
              key={day.toISOString()}
              className={cn(
                'min-h-24 border-b border-r border-border-soft p-1.5 last:border-r-0 [&:nth-child(7n)]:border-r-0',
                !inMonth && 'bg-bg-alt/40',
              )}
            >
              <div className="mb-1 flex justify-end">
                <span
                  className={cn(
                    'grid size-6 place-items-center rounded-full text-xs',
                    isToday
                      ? 'bg-brand-grad font-semibold text-primary-fg'
                      : inMonth
                        ? 'text-fg'
                        : 'text-dim',
                  )}
                >
                  {day.getDate()}
                </span>
              </div>
              <ul className="space-y-1">
                {dayOccs.slice(0, 3).map((occ) => (
                  <li key={occ.id}>
                    <div
                      className={cn(
                        'truncate rounded-md px-1.5 py-0.5 text-[11px] leading-tight',
                        occ.status === 'canceled'
                          ? 'bg-danger/10 text-danger line-through'
                          : 'bg-card-hover text-fg',
                      )}
                      title={`${occ.allDay ? 'All day' : formatTime(occ.startsAt)} — ${occ.title}`}
                    >
                      {!occ.allDay && (
                        <span className="mr-1 text-dim">{formatTime(occ.startsAt)}</span>
                      )}
                      {occ.title}
                    </div>
                  </li>
                ))}
                {dayOccs.length > 3 && (
                  <li className="px-1.5 text-[11px] text-dim">+{dayOccs.length - 3} more</li>
                )}
              </ul>
            </div>
          )
        })}
      </div>
    </Card>
  )
}

function AgendaList({
  occurrences,
  rangeFrom,
  rangeTo,
}: {
  occurrences: CalendarOccurrence[]
  rangeFrom: Date
  rangeTo: Date
}) {
  const byDay = useMemo(() => bucketByDay(occurrences), [occurrences])

  // Only show days that have events, in chronological order.
  const days = useMemo(() => {
    const out: Date[] = []
    for (let d = startOfDay(rangeFrom); d < rangeTo; d = addDays(d, 1)) {
      if (byDay.has(dayKey(d))) out.push(new Date(d))
    }
    return out
  }, [byDay, rangeFrom, rangeTo])

  if (days.length === 0) {
    return (
      <Card className="px-6 py-20 text-center text-sm text-muted">
        No events scheduled in this range.
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {days.map((day) => {
        const dayOccs = (byDay.get(dayKey(day)) ?? [])
          .slice()
          .sort((a, b) => +new Date(a.startsAt) - +new Date(b.startsAt))
        return (
          <section key={day.toISOString()} aria-label={formatDayHeading(day)}>
            <h3 className="mb-2 text-sm font-semibold text-muted">{formatDayHeading(day)}</h3>
            <Card className="divide-y divide-border-soft p-0">
              {dayOccs.map((occ) => (
                <div key={occ.id} className="flex items-start gap-4 px-4 py-3">
                  <div className="w-20 shrink-0 pt-0.5 text-xs font-medium text-muted">
                    {occ.allDay ? 'All day' : formatTime(occ.startsAt)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          'font-medium',
                          occ.status === 'canceled' && 'text-danger line-through',
                        )}
                      >
                        {occ.title}
                      </span>
                      {occ.status !== 'scheduled' && (
                        <Badge
                          variant={statusVariant[occ.status] ?? 'neutral'}
                          className="capitalize"
                        >
                          {occ.status}
                        </Badge>
                      )}
                    </div>
                    {occ.description && (
                      <p className="mt-0.5 line-clamp-2 text-sm text-muted">{occ.description}</p>
                    )}
                    {(occ.locationText || occ.siteName) && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-dim">
                        <MapPin className="size-3" />
                        {[occ.locationText, occ.siteName].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </Card>
          </section>
        )
      })}
    </div>
  )
}
